import { useEffect, useState } from "react";
import { Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

export const P = "/studio-portal";

export const openPdf = async (path) => {
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
  } catch (e) {
    let msg = "Could not open the PDF";
    try { msg = apiError(JSON.parse(await e.response.data.text()).detail); } catch (_) { /* keep default */ }
    toast.error(msg);
  }
};

const TONES = {
  Placed: "bg-slate-100 text-slate-700", "Proforma Sent": "bg-sky-100 text-sky-800",
  "Payment Received": "bg-emerald-100 text-emerald-800", Dispatched: "bg-brand-deep text-white",
  Cancelled: "bg-red-100 text-red-700", Submitted: "bg-sky-100 text-sky-800",
  "Awaiting Payment Approval": "bg-amber-100 text-amber-800", "Payment Approved": "bg-sky-100 text-sky-800",
  "Payment Rejected": "bg-red-100 text-red-700", Returned: "bg-red-100 text-red-700",
  Registered: "bg-emerald-100 text-emerald-800", Sent: "bg-brand-deep text-white",
};

export const StatusPill = ({ status, testid }) => (
  <Badge data-testid={testid} className={`border-0 font-medium ${TONES[status] || "bg-slate-100 text-slate-700"}`}>
    {status}
  </Badge>
);

export const AuthImg = ({ path, className }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url = "";
    api.get(`${P}/files/${path}`, { responseType: "blob" })
      .then(({ data }) => { url = URL.createObjectURL(data); setSrc(url); })
      .catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [path]);
  if (!src) return <div className={`animate-pulse bg-slate-100 ${className}`} />;
  return <img src={src} alt="upload" className={`object-cover ${className}`} />;
};

export const PhotoUploader = ({ label, value = [], onChange, testid, max = 6 }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    setBusy(true);
    const out = [...value];
    for (const f of Array.from(files).slice(0, max - value.length)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`${P}/upload`, fd);
        out.push(data.path);
      } catch (e) {
        toast.error(apiError(e.response?.data?.detail));
      }
    }
    onChange(out);
    setBusy(false);
  };
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-slate-700">{label}</p>
      <div className="flex flex-wrap gap-2">
        {value.map((p) => (
          <div key={p} className="relative">
            {p.endsWith(".pdf") ? (
              <div className="flex h-20 w-20 items-center justify-center rounded-md bg-slate-100 text-xs">PDF</div>
            ) : (
              <AuthImg path={p} className="h-20 w-20 rounded-md" />
            )}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== p))}
              className="absolute -right-1.5 -top-1.5 rounded-full bg-white p-0.5 shadow" aria-label="Remove">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
        {value.length < max && (
          <label data-testid={testid}
            className="flex h-20 w-20 cursor-pointer flex-col items-center justify-center gap-1 rounded-md border border-dashed border-slate-300 text-xs text-slate-500 transition-colors hover:border-crimson-500 hover:text-crimson-600">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
            Add
            <input type="file" accept="image/*,application/pdf" multiple hidden
              onChange={(e) => e.target.files?.length && upload(e.target.files)} />
          </label>
        )}
      </div>
    </div>
  );
};

export const Field = ({ label, children, className = "" }) => (
  <label className={`block space-y-1.5 ${className}`}>
    <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
    {children}
  </label>
);
